import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { ArrowLeft, QrCode, Download, Printer, User, Loader2 } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

export default function VendorQRCode() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const profileQrRef = useRef(null);
  const punchQrRef = useRef(null);

  const fetchInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vendor/truck-info`);
      setData(res.data);
    } catch { /* */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const downloadQR = (ref, filename) => {
    const canvas = ref.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.download = filename;
    link.href = url;
    link.click();
    toast.success("QR code downloaded");
  };

  const downloadVCard = () => {
    if (!data) return;
    const vcard = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `FN:${data.name || ""}`,
      `ORG:${data.name || ""}`,
      data.contact_phone ? `TEL:${data.contact_phone}` : "",
      data.home_base_address ? `ADR:;;${data.home_base_address}` : "",
      data.vendor_app_link ? `URL:${data.vendor_app_link}` : "",
      `NOTE:Food truck on BirthdayDeaLight`,
      "END:VCARD",
    ].filter(Boolean).join("\n");

    const blob = new Blob([vcard], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = `${(data.name || "truck").replace(/\s+/g, "_")}.vcf`;
    link.href = url;
    link.click();
    toast.success("vCard downloaded");
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#F59E0B]" /></div>;
  }

  const bid = data?.business_id || "";
  const baseUrl = window.location.origin;
  const profileUrl = `${baseUrl}/truck/${bid}`;
  const punchUrl = `${baseUrl}/punch/${bid}`;

  return (
    <div className="min-h-screen px-4 pt-14 pb-24" data-testid="vendor-qr-code">
      <button onClick={() => navigate("/vendor")} className="flex items-center gap-1.5 text-white/40 text-xs mb-4 hover:text-white/60 min-h-[44px]">
        <ArrowLeft className="w-4 h-4" /> Dashboard
      </button>
      <h1 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
        <QrCode className="w-5 h-5 text-[#F59E0B]" /> QR Codes
      </h1>

      {/* Profile QR */}
      <section className="bg-[#2C2C2E] rounded-xl p-5 mb-3" data-testid="profile-qr-section">
        <p className="text-sm font-bold text-white mb-1">Truck Profile QR</p>
        <p className="text-[10px] text-white/40 mb-4">Scan to find us on BirthdayDeaLight</p>

        <div ref={profileQrRef} className="bg-white rounded-xl p-5 flex items-center justify-center mb-4">
          <QRCodeCanvas
            value={profileUrl}
            size={200}
            level="H"
            imageSettings={{
              src: "/favicon.ico",
              height: 30,
              width: 30,
              excavate: true,
            }}
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => downloadQR(profileQrRef, `${data?.name || "truck"}_profile_qr.png`)}
            className="flex-1 py-2.5 rounded-xl bg-[#F59E0B]/20 text-[#F59E0B] text-xs font-bold flex items-center justify-center gap-1.5"
            data-testid="download-profile-qr"
          >
            <Download className="w-3.5 h-3.5" /> Download
          </button>
        </div>
        <p className="text-[9px] text-white/20 mt-2 flex items-center gap-1">
          <Printer className="w-3 h-3" /> Print and display on your truck window or menu board
        </p>
      </section>

      {/* Punch Card QR */}
      <section className="bg-[#2C2C2E] rounded-xl p-5 mb-3" data-testid="punch-qr-section">
        <p className="text-sm font-bold text-white mb-1">Punch Card QR</p>
        <p className="text-[10px] text-white/40 mb-4">Scan to earn loyalty points</p>

        <div ref={punchQrRef} className="bg-white rounded-xl p-5 flex items-center justify-center mb-4">
          <QRCodeCanvas
            value={punchUrl}
            size={200}
            level="H"
            imageSettings={{
              src: "/favicon.ico",
              height: 30,
              width: 30,
              excavate: true,
            }}
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => downloadQR(punchQrRef, `${data?.name || "truck"}_punch_qr.png`)}
            className="flex-1 py-2.5 rounded-xl bg-[#F59E0B]/20 text-[#F59E0B] text-xs font-bold flex items-center justify-center gap-1.5"
            data-testid="download-punch-qr"
          >
            <Download className="w-3.5 h-3.5" /> Download
          </button>
        </div>
        <p className="text-[9px] text-white/20 mt-2 flex items-center gap-1">
          <Printer className="w-3 h-3" /> Print and display at your service window
        </p>
      </section>

      {/* vCard */}
      <section className="bg-[#2C2C2E] rounded-xl p-5" data-testid="vcard-section">
        <p className="text-sm font-bold text-white mb-1 flex items-center gap-2">
          <User className="w-4 h-4 text-[#F59E0B]" /> Your Digital Business Card
        </p>
        <div className="bg-[#1C1C1E] rounded-lg p-3 mb-3 mt-3 space-y-1">
          <p className="text-xs text-white font-medium">{data?.name || "Your Truck"}</p>
          {data?.contact_phone && <p className="text-[10px] text-white/40">{data.contact_phone}</p>}
          {data?.home_base_address && <p className="text-[10px] text-white/40">{data.home_base_address}</p>}
          {data?.vendor_app_link && <p className="text-[10px] text-[#F59E0B] truncate">{data.vendor_app_link}</p>}
        </div>
        <div className="flex gap-2">
          <button
            onClick={downloadVCard}
            className="flex-1 py-2.5 rounded-xl bg-[#F59E0B] text-[#1C1C1E] text-xs font-bold flex items-center justify-center gap-1.5"
            data-testid="download-vcard"
          >
            <Download className="w-3.5 h-3.5" /> Download vCard
          </button>
          <button
            onClick={() => navigate("/vendor/truck-info")}
            className="py-2.5 px-4 rounded-xl bg-[#1C1C1E] text-white/60 text-xs font-bold border border-white/10"
            data-testid="edit-vcard"
          >
            Edit
          </button>
        </div>
      </section>
    </div>
  );
}
